"use client";

import React from 'react';
import Image from 'next/image';
import { useLocale } from "@/context/LocaleContext";

export default function QRCheckinFeature() {
  const { t } = useLocale(); 

  return (
    <div className="w-full">
      <section className="w-full bg-gray-50 py-16 mt-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[52%] flex justify-center">
                <div className="relative w-full max-w-[550px] aspect-[4/5]">
                <Image 
                    src="/images/cta/qr-code.webp" 
                    alt="Cara kerja QR Code Check-In" 
                    fill 
                    className="object-contain drop-shadow-2xl scale-105"
                />
                </div>
            </div>

            <div className="w-full md:w-[48%]">
                <h2 className="text-3xl md:text-4xl lg:text-[2.4rem] font-extrabold text-gray-900 mb-8 leading-tight">
                  {t("QRCheckin.how_it_works") || "Bagaimana cara kerjanya?"}
                </h2>
                
                <div className="space-y-6 lg:space-y-8">
                <div className="flex items-start gap-4 lg:gap-5">
                    <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">1.</span>
                    <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.step1_title") || "QR Code untuk Setiap Tamu"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                        {t("QRCheckin.step1_desc") || "Setiap undangan dilengkapi QR Code unik yang hanya berlaku untuk tamu tersebut."}
                    </p>
                    </div>
                </div>

                <div className="flex items-start gap-4 lg:gap-5">
                    <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">2.</span>
                    <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.step2_title") || "Scan di Lokasi Acara"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                        {t("QRCheckin.step2_desc") || "Saat tiba di lokasi, tamu cukup menunjukkan QR Code untuk dipindai oleh panitia."}
                    </p>
                    </div>
                </div>

                <div className="flex items-start gap-4 lg:gap-5">
                    <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">3.</span>
                    <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.step3_title") || "Kehadiran Tercatat Otomatis"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                        {t("QRCheckin.step3_desc") || "Setelah QR Code dipindai, kehadiran tamu langsung tercatat di sistem secara realtime."}
                    </p>
                    </div>
                </div>
                </div>
            </div>

            </div>
        </div>
      </section>


      <section className="w-full bg-white py-16 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col-reverse md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[55%]">
              <h2 
                className="text-3xl md:text-4xl lg:text-[2.2rem] font-extrabold text-gray-900 mb-6 leading-tight"
                dangerouslySetInnerHTML={{ __html: t("QRCheckin.why_use_title") || "Kenapa Menggunakan<br/>QR Code Check-In?" }}
              />
              
              <p className="text-gray-700 text-[15px] md:text-[17px] mb-4 font-medium">
                {t("QRCheckin.without_qr_desc") || "Tanpa Sistem Check-In Digital, sering terjadi:"}
              </p>
              
              <ul className="list-disc pl-5 space-y-2 text-gray-700 text-[15px] md:text-[16px] lg:text-[17px] mb-6 font-medium">
                <li>{t("QRCheckin.without_qr_li_1") || "Antrean panjang di pintu masuk"}</li>
                <li>{t("QRCheckin.without_qr_li_2") || "Kesalahan pencatatan tamu"}</li>
                <li>{t("QRCheckin.without_qr_li_3") || "Sulit mengetahui jumlah tamu yang sudah hadir"}</li>
              </ul>
              
              <p className="text-gray-700 text-[15px] md:text-[17px] font-medium">
                {t("QRCheckin.qr_solves_desc") || "QR Code Check-In membantu mengatasi masalah tersebut."}
              </p>
            </div>

            <div className="w-full md:w-[45%] flex justify-center md:justify-end">
              <div className="relative w-full md:max-w-[350px] max-w-[250px] aspect-square">
                <Image 
                  src="/images/features/landing-page/why-qr.webp" 
                  alt="Ilustrasi kenapa pakai QR Code" 
                  fill 
                  className="object-contain mix-blend-multiply" 
                />
              </div>
            </div>

          </div>
        </div>
      </section>

     <section className="w-full bg-gray-50 py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
            <h2 className="text-3xl md:text-4xl lg:text-[2.8rem] font-bold text-gray-900 mb-16 md:mb-20 text-center tracking-tight">
              {t("QRCheckin.main_benefits_title") || "Keuntungan utama"}
            </h2>

            <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[48%] flex justify-center relative">
                <div className="relative w-full max-w-[600px] aspect-[4/5]">
                    <Image 
                        src="/images/features/landing-page/benefit-qr.webp"
                        alt="Keuntungan utama QR Check-In" 
                        fill 
                        className="object-contain drop-shadow-2xl md:scale-125 transition-transform duration-500" 
                    />
                </div>
            </div>

            <div className="w-full md:w-[55%]">
                <div className="relative">
                <div className="absolute left-[15px] top-4 bottom-[-15px] w-[2px] bg-[#ffcaca] z-0"></div>
                <div className="flex items-start gap-4 lg:gap-6 relative z-10 pb-8 lg:pb-10">
                    <div className="w-8 h-8 rounded-full bg-[#E85C57] text-white flex items-center justify-center font-bold shrink-0 mt-0.5 shadow-md">
                    1
                    </div>
                    <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.benefit1_title") || "Proses Check-In Lebih Cepat & Tertib"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                        {t("QRCheckin.benefit1_desc") || "Tamu tidak perlu menulis manual atau menunggu lama, sehingga alur masuk acara lebih lancar."}
                    </p>
                    </div>
                </div>

                <div className="flex items-start gap-4 lg:gap-6 relative z-10 pb-8 lg:pb-10">
                    <div className="w-8 h-8 rounded-full bg-[#FCE8E8] border-[2px] border-[#ffcaca] text-[#E85C57] flex items-center justify-center font-bold shrink-0 mt-0.5">
                    2
                    </div>
                    <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.benefit2_title") || "Data kehadiran Tercatat Otomatis"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px]">
                        {t("QRCheckin.benefit2_desc") || "Setiap tamu yang hadir langsung tercatat di sistem, tanpa input manual dari panitia."}
                    </p>
                    </div>
                </div>

                <div className="flex items-start gap-4 lg:gap-6 relative z-10 pb-8 lg:pb-10">
                    <div className="w-8 h-8 rounded-full bg-[#E85C57] text-white flex items-center justify-center font-bold shrink-0 mt-0.5 shadow-md">
                    3
                    </div>
                    <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.benefit3_title") || "Cocok untuk Acara Formal & Profesional"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px] mb-2">
                        {t("QRCheckin.benefit3_desc") || "Fitur ini sangat ideal untuk:"}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700 text-[15px] md:text-[16px] lg:text-[17px]">
                        <li>{t("QRCheckin.benefit3_li_1") || "Pernikahan dengan banyak tamu"}</li>
                        <li>{t("QRCheckin.benefit3_li_2") || "Intimate Wedding dengan kontrol tamu"}</li>
                        <li>{t("QRCheckin.benefit3_li_3") || "Event Profesional dan Corporate"}</li>
                    </ul>
                    </div>
                </div>

                </div> 
                <div className="flex items-start gap-4 lg:gap-6 relative z-10">
                <div className="w-8 h-8 rounded-full bg-[#FCE8E8] border-[2px] border-[#ffcaca] text-[#E85C57] flex items-center justify-center font-bold shrink-0 mt-0.5">
                    4
                </div>
                <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("QRCheckin.benefit4_title") || "Terintegrasi dengan Sistem Undangan"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px] mb-2">
                      {t("QRCheckin.benefit4_desc") || "QR Code Check-In buku tamu terhubung langsung dengan:"}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700 text-[15px] md:text-[16px] lg:text-[17px] mb-3">
                        <li className='font-semibold'>{t("QRCheckin.benefit4_li_1") || "Custom RSVP"}</li>
                        <li className='font-semibold'>{t("QRCheckin.benefit4_li_2") || "Reminder Acara Otomatis"}</li>
                        <li className='font-semibold'>{t("QRCheckin.benefit4_li_3") || "Laporan kehadiran tamu"}</li>
                    </ul>
                    <p className="text-gray-800 leading-relaxed text-[15px] md:text-[16px] lg:text-[17px] font-normal">
                      {t("QRCheckin.benefit4_footer") || "Semua data kehadiran tersimpan rapi dalam satu platform."}
                    </p>
                </div>
                </div>

            </div>
            </div>
        </div>
     </section>

    </div>
  );
}